import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { sfx } from "../lib/sound";

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true)) ||
  false;

const isIos = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios/i.test(navigator.userAgent);

// Bannière d'installation PWA — Android/desktop via beforeinstallprompt, iOS via astuce manuelle.
export const InstallPrompt = () => {
  const { t } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || sessionStorage.getItem("de_install_dismissed")) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS : pas d'event → on affiche l'astuce manuelle après un court délai
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => {
        setIosHint(true);
        setShow(true);
      }, 2500);
    }

    const onInstalled = () => setShow(false);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    sfx.click();
    setShow(false);
    try {
      sessionStorage.setItem("de_install_dismissed", "1");
    } catch (e) {
      /* ignore */
    }
  };

  const install = async () => {
    if (!deferred) return;
    sfx.click();
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      /* ignore */
    }
    setDeferred(null);
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          data-testid="install-prompt"
          className="fixed inset-x-0 bottom-[72px] z-[130] flex justify-center px-3 xl:bottom-6"
        >
          <div className="relative flex w-full max-w-md items-center gap-3 overflow-hidden rounded-2xl border border-white/12 bg-[#15152a]/95 p-3 pr-10 shadow-2xl backdrop-blur-md">
            <img
              src="/icon-192.png"
              alt=""
              className="h-11 w-11 flex-shrink-0 rounded-xl"
            />
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-bold text-white">{t("installTitle")}</p>
              {iosHint ? (
                <p className="mt-0.5 flex items-center gap-1 text-[11px] leading-snug text-white/60">
                  {t("installIos")}
                  <Share size={12} className="inline text-[#f093fb]" />
                </p>
              ) : (
                <p className="mt-0.5 text-[11px] leading-snug text-white/60">{t("installDesc")}</p>
              )}
            </div>
            {!iosHint && (
              <button
                onClick={install}
                data-testid="install-button"
                className="flex flex-shrink-0 items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#f093fb] to-[#f5576c] px-4 py-2.5 font-display text-sm font-bold text-white transition-transform active:scale-95"
              >
                <Download size={16} /> {t("installBtn")}
              </button>
            )}
            <button
              onClick={dismiss}
              data-testid="install-dismiss"
              className="absolute right-2 top-2 text-white/40 transition-colors hover:text-white"
              aria-label={t("installLater")}
            >
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
